import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  XAxis,
  YAxis,
} from 'recharts'
import { useTranslation } from '@kostbar/i18n'
import type { ChartConfig } from '@/components/ui/chart'
import type {
  ReportMeasure,
  ReportRow,
} from '@/features/reports/api/use-sales-report-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart'
import { formatCurrency, formatNumber } from '@/features/reports/lib/format'
import {
  formatDayKey,
  formatHourKey,
  isWeekend,
} from '@/features/reports/lib/dimension-labels'
import { Skeleton } from '@/components/ui/skeleton'

interface RevenueTrendCardProps {
  rows: Array<ReportRow>
  measure: ReportMeasure
  /** `time_day` for a period, `time_hour` for the running day. */
  granularity: 'day' | 'hour'
  /** The day or hour still in progress — drawn lighter, because it is not a full bucket. */
  runningKey?: string | null
  comparisonLabel: string
  loading?: boolean
}

interface Point {
  key: string
  label: string
  value: number
  previous: number | null
  weekend: boolean
  running: boolean
}

/**
 * "Are we growing" as a picture (CHG-2026-09-manager-reporting).
 *
 * Bars for the period, the comparison window as a dashed line on the **same** axis. Never a second
 * y-scale: two scales let a chart invent a correlation that is not in the data.
 *
 * Three pieces of context the store's own shape demands: weekends are banded, because this is a
 * workplace lunch operation that trades near zero on Saturday and Sunday and an unmarked pair of
 * short bars reads as a collapse; the still-running bucket is drawn lighter, because it is not a
 * full day; and on the running day a "now" line marks where the data stops.
 */
export function RevenueTrendCard({
  rows,
  measure,
  granularity,
  runningKey,
  comparisonLabel,
  loading,
}: RevenueTrendCardProps) {
  const { t } = useTranslation('reports')

  const points: Array<Point> = rows.map((row) => ({
    key: row.key,
    label:
      granularity === 'day' ? formatDayKey(row.key) : formatHourKey(row.key),
    value: measure === 'units' ? row.units : row.revenueCents,
    previous:
      measure === 'units' ? row.previousUnits : row.previousRevenueCents,
    weekend: granularity === 'day' && isWeekend(row.key),
    running: row.key === runningKey,
  }))

  const config: ChartConfig = {
    value: {
      label: t(measure === 'units' ? 'measure.units' : 'measure.revenue'),
      color: 'var(--magnitude)',
    },
    previous: { label: comparisonLabel, color: 'var(--muted-foreground)' },
  }

  const format = (value: number) =>
    measure === 'units' ? formatNumber(value) : formatCurrency(value)

  // Consecutive weekend days become one band, so a Saturday/Sunday pair is a single grey field
  // rather than two adjacent rectangles with a seam down the middle.
  const bands = weekendBands(points)

  return (
    <Card className="min-w-0">
      <CardHeader>
        <CardTitle className="text-sm font-semibold">
          {granularity === 'day' ? t('trend.title') : t('trend.hourlyTitle')}
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {granularity === 'day'
            ? t('trend.subtitle')
            : t('trend.hourlySubtitle')}
        </p>
      </CardHeader>
      <CardContent className="min-w-0">
        {loading ? (
          <Skeleton className="h-[220px] w-full" />
        ) : points.length === 0 ? (
          <p className="py-16 text-center text-sm text-muted-foreground">
            {t('empty')}
          </p>
        ) : (
          <ChartContainer
            config={config}
            className="aspect-auto h-[220px] w-full"
          >
            <ComposedChart
              data={points}
              margin={{ top: 8, right: 8, left: 8, bottom: 0 }}
            >
              {bands.map((band) => (
                <ReferenceArea
                  key={band.from}
                  x1={band.from}
                  x2={band.to}
                  fill="var(--muted)"
                  fillOpacity={0.7}
                  ifOverflow="extendDomain"
                />
              ))}
              <CartesianGrid vertical={false} strokeDasharray="0" />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                interval="preserveStartEnd"
                minTickGap={12}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                width={56}
                tickFormatter={(value: number) => format(value)}
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    formatter={(value) => format(Number(value))}
                  />
                }
              />
              {/* 4px rounded data-end, square at the baseline; capped so a short window keeps air
                  between the bars instead of filling the band. */}
              <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={24}>
                {points.map((point) => (
                  <Cell
                    key={point.key}
                    fill="var(--magnitude)"
                    fillOpacity={point.running ? 0.45 : 1}
                  />
                ))}
              </Bar>
              <Line
                dataKey="previous"
                type="linear"
                stroke="var(--muted-foreground)"
                strokeWidth={2}
                strokeDasharray="4 3"
                dot={false}
                connectNulls
              />
              {runningKey && granularity === 'hour' ? (
                <ReferenceLine
                  x={points.find((p) => p.running)?.label}
                  stroke="var(--foreground)"
                  strokeOpacity={0.35}
                  label={{
                    value: t('trend.now'),
                    position: 'insideTopRight',
                    fontSize: 11,
                  }}
                />
              ) : null}
            </ComposedChart>
          </ChartContainer>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          {granularity === 'day'
            ? t('trend.legendHint')
            : t('trend.hourlyHint')}
        </p>
      </CardContent>
    </Card>
  )
}

function weekendBands(
  points: Array<Point>,
): Array<{ from: string; to: string }> {
  const bands: Array<{ from: string; to: string }> = []
  let open: { from: string; to: string } | null = null
  for (const point of points) {
    if (point.weekend) {
      if (open) open.to = point.label
      else open = { from: point.label, to: point.label }
    } else if (open) {
      bands.push(open)
      open = null
    }
  }
  if (open) bands.push(open)
  return bands
}
